import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { login, logout, checkAuth } from '@/shared/api/AdminApi';

interface AuthUser {
	id: string;
	username: string;
}

interface AdminAuthState {
	// State
	user: AuthUser | null;
	isAuthenticated: boolean;
	isInitialized: boolean;
	isLoading: boolean;
	error: string | null;

	// Actions
	initialize: () => Promise<void>;
	loginUser: (
		username: string,
		password: string,
		rememberMe?: boolean,
	) => Promise<boolean>;
	logoutUser: () => Promise<void>;
	clearError: () => void;
}

export const useAdminAuthStore = create<AdminAuthState>()(
	persist(
		(set, get) => ({
			// Initial state
			user: null,
			isAuthenticated: false,
			isInitialized: false,
			isLoading: false,
			error: null,

			// Initialize auth state on app load
			initialize: async () => {
				if (get().isInitialized) {
					return;
				}

				set({ isLoading: true });

				try {
					const response = await checkAuth();
					set({
						user: response.user,
						isAuthenticated: response.authenticated,
						isInitialized: true,
						isLoading: false,
						error: null,
					});
				} catch (error) {
					console.error('Failed to check auth:', error);
					set({
						user: null,
						isAuthenticated: false,
						isInitialized: true,
						isLoading: false,
						error: null,
					});
				}
			},

			// Login
			loginUser: async (username, password, rememberMe = false) => {
				set({ isLoading: true, error: null });

				try {
					const response = await login({ username, password, rememberMe });

					if (response.success && response.user) {
						set({
							user: response.user,
							isAuthenticated: true,
							isLoading: false,
							error: null,
						});
						return true;
					} else {
						set({
							user: null,
							isAuthenticated: false,
							isLoading: false,
							error: response.error || 'Login failed',
						});
						return false;
					}
				} catch (error) {
					console.error('Login error:', error);
					set({
						user: null,
						isAuthenticated: false,
						isLoading: false,
						error: 'Network error. Please try again.',
					});
					return false;
				}
			},

			// Logout
			logoutUser: async () => {
				set({ isLoading: true });

				try {
					await logout();
					set({
						user: null,
						isAuthenticated: false,
						isLoading: false,
						error: null,
					});
				} catch (error) {
					console.error('Logout error:', error);
					// Force logout client-side even if server fails
					set({
						user: null,
						isAuthenticated: false,
						isLoading: false,
						error: null,
					});
				}
			},

			// Clear error
			clearError: () => {
				set({ error: null });
			},
		}),
		{
			name: 'admin-auth-storage',
			partialize: (state) => ({
				// Only persist minimal data
				isAuthenticated: state.isAuthenticated,
			}),
		},
	),
);
